import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form } from '@/components/ui/form';
import { useToast } from '@/hooks/use-toast';
import { getEventsList, getUserSelectedEvents } from '@/routes/trackings/tracking-utils';
import { IntegrationTypeV2, IntegrationV2Query, KlaviyoCredentialsModel } from '@/gql/graphql';
import { useParams } from 'react-router';
import { useEffect } from 'react';
import { SaveButton } from '../../components/save-button';
import { useIntegrationStore } from '../../stores/tracking-store';
import { useApolloClient } from '@apollo/client';
import {
  FormLayout,
  FormSection,
  PasswordField
} from './../../components/tracking-details-form/base';
import {
  SmartQualifySection,
  ConsentModeSection,
  FormHeaderSection,
  CustomEventsSection
} from '../../components/tracking-details-form/shared-sections';

const formSchema = z.object({
  credentials: z.object({
    apiKey: z.string().min(1, 'API Key is required.')
  }),
  consentMode: z.boolean(),
  customEvents: z.array(z.string()).optional(),
  smartQualify: z.boolean()
});

const eventsList = getEventsList(IntegrationTypeV2.Klaviyo);

export const KlaviyoSettingsForm = ({ onContinue }: { onContinue: () => void }) => {
  const { toast } = useToast();
  const params = useParams<{ id: string; slug: string }>();
  const { integration, isLoading, updateSettings } = useIntegrationStore();
  const client = useApolloClient();

  const isInitialSetup = !(integration?.credentials as KlaviyoCredentialsModel | undefined)?.apiKey;

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      credentials: {
        apiKey: ''
      },
      consentMode: false,
      smartQualify: true,
      customEvents: []
    }
  });

  useEffect(() => {
    if (!isLoading && integration) {
      const credentials = integration.credentials as KlaviyoCredentialsModel | undefined;

      form.reset({
        credentials: {
          apiKey: credentials?.apiKey ?? ''
        },
        consentMode: integration.consentMode ?? false,
        smartQualify: integration.smartQualify ?? true,
        customEvents: getUserSelectedEvents({
          integrationV2: integration as IntegrationV2Query['integrationV2']
        })
      });
    }
  }, [integration, form.reset]);

  const { isDirty } = form.formState;

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      await updateSettings(client, {
        id: params.id!,
        customEvents: values.customEvents ?? [],
        consentMode: values.consentMode,
        smartQualify: values.smartQualify,
        credentials: values.credentials
      });
      toast({
        title: 'Form submitted',
        description: 'Your Klaviyo integration settings have been saved.',
        variant: 'default'
      });
      onContinue();
    } catch (_) {
      toast({
        title: 'Error',
        description: 'Failed to save Klaviyo integration settings.',
        variant: 'destructive'
      });
    }
  };

  return (
    <Form {...form}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void form.handleSubmit(onSubmit)(e);
        }}
      >
        <FormLayout>
          <FormHeaderSection title="Klaviyo Connection">
            In order to send transactions data to this destination, we need the following
            information from your Klaviyo account.
          </FormHeaderSection>
          <FormSection columns={2}>
            <PasswordField form={form} name="credentials.apiKey" label="Klaviyo API Key" />
          </FormSection>
          <ConsentModeSection form={form} />
          <SmartQualifySection form={form} />
          <CustomEventsSection
            form={form}
            eventsList={eventsList}
            getUserSelectedEvents={() =>
              getUserSelectedEvents({
                integrationV2: integration as IntegrationV2Query['integrationV2']
              })
            }
          />
          <FormSection>
            <SaveButton isDirty={isDirty} isUpdating={isLoading} isInitialSetup={isInitialSetup} />
          </FormSection>
        </FormLayout>
      </form>
    </Form>
  );
};
